// client/services/customEmailData.js

/**
 * Prepares dynamic email content based on event type
 *
 * @param {Object} params
 * @param {Object} params.user - User object from Sharetribe SDK
 * @param {string} params.planName - Name of the plan (e.g., "Grower", "Plus")
 * @param {string} params.event - Type of email event ("purchase", "update", "cancel", etc.)
 * @returns {Object} emailData - { to, subject, text, html }
 */
export const customEmailData = ({ user, planName, event }) => {
  const userName = user?.attributes?.profile?.firstName || 'there';
  const to = user?.attributes?.email;

  let subject = '';
  let text = '';
  let html = '';

  switch (event) {
    case 'purchase':
      subject = `Your ${planName} plan is now active 🎉`;
      text = `Hi ${userName}, your ${planName} subscription has been successfully activated. Thank you for choosing us!`;
      html = `
        <h2>Welcome, ${userName}!</h2>
        <p>We're excited to let you know that your <b>${planName}</b> plan is now active.</p>
        <p>Thank you for trusting us — let’s make great things happen together!</p>
      `;
      break;

    case 'update':
      subject = `Your subscription has been updated 🔄`;
      text = `Hi ${userName}, your subscription plan has been successfully updated to ${planName}.`;
      html = `
        <h2>Hi ${userName},</h2>
        <p>Your subscription has been updated to <b>${planName}</b>.</p>
        <p>Enjoy the new features and benefits of your updated plan!</p>
      `;
      break;

    case 'cancel':
      subject = `Your subscription has been canceled 😢`;
      text = `Hi ${userName}, your ${planName} plan has been canceled. We're sorry to see you go.`;
      html = `
        <h2>Hi ${userName},</h2>
        <p>Your <b>${planName}</b> subscription has been canceled.</p>
        <p>We're sorry to see you go, but you're always welcome back anytime.</p>
      `;
      break;

    case 'renewal':
      subject = `Your subscription has been renewed 🔁`;
      text = `Hi ${userName}, your ${planName} subscription has been renewed successfully.`;
      html = `
        <h2>Hi ${userName},</h2>
        <p>Your <b>${planName}</b> plan has been renewed for another billing period.</p>
        <p>Thank you for staying with us!</p>
      `;
      break;

    default:
      subject = `Update from our team`;
      text = `Hi ${userName}, here’s an update about your account.`;
      html = `
        <h2>Hi ${userName},</h2>
        <p>This is a quick update from our team regarding your account.</p>
      `;
      break;
  }

  return {
    to,
    subject,
    text,
    html,
  };
};

export const shopperEmailData = ({ user, event }) => {
  const userName = user?.attributes?.profile?.firstName || 'there';
  const to = user?.attributes?.email;
  const profileUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL + "/profile-settings"

  let subject = '';
  let text = '';
  let html = '';

  switch (event) {
    case 'welcome-shopper':
      subject = `Welcome to our marketplace, ${userName}! Let’s get you started 👋`;

      text = `
Hi ${userName},

Welcome! Your account has been created successfully 🎉

To get the best experience, we recommend completing your profile. A complete profile helps farmers trust you and makes checkout faster.

Just add:
- Your name & profile photo
- Contact details
- Preferences (so we can personalize your experience)

Complete your profile now and start exploring amazing listings!

See you inside,
The Team
    `;

      html = `
        <h2>Welcome, ${userName}! 🎉</h2>

        <p>We’re excited to have you join our marketplace.</p>

        <p>
          Before you start shopping, take a moment to
          <b>complete your profile</b>. It helps farmers trust you and ensures a smooth experience.
        </p>

        <p>
          <b>It only takes a minute</b> and makes a big difference ✨
        </p>

        <p>
          👉 <a href="${profileUrl}" style="color:#2563eb; font-weight:bold;">
            Complete your profile now
          </a>
        </p>

        <p>Happy exploring,<br/>The Team</p>
      `;
      break;

    default:
      subject = `Update from our team`;
      text = `Hi ${userName}, here’s an update about your account.`;
      html = `
        <h2>Hi ${userName},</h2>
        <p>This is a quick update from our team regarding your account.</p>
      `;
      break;
  }

  return {
    to,
    subject,
    text,
    html,
  };
};
